import { useApolloClient } from '@apollo/client';
import { Stack } from '@mui/material';
import { useCallback } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { PlusIcon } from '@/components/icons/PlusIcon';
import { useModalState } from '@/hooks/useModalState';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { CashflowDrawer } from '@/modules/cashflows/CashflowDrawer/CashflowDrawer';
import { CashflowsTable } from '@/modules/cashflows/CashflowTable/CashflowTable';
import { ClientProfileSummaryPanel_ClientProfileFragment } from '@/modules/summaryPanels/ClientProfileSummaryPanel/graphql/ClientProfileSummaryPanel.generated';
import { getNodes } from '@/utils/graphqlUtils';

import { EntityDetail_EntityFragment } from '../graphql/EntityDetailPage.generated';

export interface CashFlowsCardProps {
  entity?: EntityDetail_EntityFragment;
  individual?: ClientProfileSummaryPanel_ClientProfileFragment;
}

/**
 * This component is used to display the cash flows for an entity or individual
 * in the estate waterfall side panel.
 * It also handles the logic for refetching the cash flows when a cash flow is deleted.
 */
export function CashFlowsCard({ entity, individual }: CashFlowsCardProps) {
  const canEdit = useCanEditHouseholdData();
  const client = useApolloClient();
  const [{ isModalOpen }, { openModal, closeModal }] = useModalState();
  const cashflows = getNodes(entity?.cashflows);
  const entityId = entity?.id;
  const individualId = individual?.id;

  const refetch = useCallback(() => {
    void client.refetchQueries({
      include: 'active',
      updateCache(cache) {
        cache.evict({ fieldName: 'cashflows' });
      },
    });
  }, [client]);

  return (
    <Stack spacing={2}>
      <CashflowsTable
        cashflows={cashflows || []}
        entityId={entityId}
        individualId={individualId}
        refetch={refetch}
        emptyStateFill="flex"
      />
      {!cashflows?.length || !canEdit ? null : (
        <>
          <Button
            variant="secondary"
            size="sm"
            fullWidth
            startIcon={PlusIcon}
            onClick={openModal}
          >
            Add cash flow
          </Button>
          <CashflowDrawer
            isOpen={isModalOpen}
            onClose={closeModal}
            recipient={entityId || individualId}
            cashflowId={null}
          />
        </>
      )}
    </Stack>
  );
}
